import { useState } from "react";

export function RecurringEventScheduler({ onDone }) {
  const [isOpen, setIsOpen] = useState(false);
  const [state, setState] = useState({
    frequency: "Weekly",
    interval: 1,
    daysOfWeek: ["Th"],
    startDate: "2016-10-03",
    endType: "never",
    occurrences: 10,
    endDate: "",
  });

  const handleDayToggle = (day) => {
    if (state.daysOfWeek.includes(day)) {
      setState({
        ...state,
        daysOfWeek: state.daysOfWeek.filter((d) => d !== day),
      });
    } else {
      setState({
        ...state,
        daysOfWeek: [...state.daysOfWeek, day],
      });
    }
  };

  const getSummary = () => {
    const frequencyText =
      state.interval === 1
        ? state.frequency
        : `Every ${state.interval} ${state.frequency.toLowerCase()}s`;

    let daysText = "";
    if (state.frequency === "Weekly" && state.daysOfWeek.length > 0) {
      daysText = ` on ${state.daysOfWeek.join(", ")}`;
    }

    return `${frequencyText}${daysText}`;
  };

  const getCronExpression = () => {
    // Base cron structure: second, minute, hour, day-of-month, month, day-of-week
    let seconds = "0";
    let minutes = "*";
    let hours = "*";
    let dayOfMonth = "*";
    let month = "*";
    let dayOfWeek = "*";

    const freq = state.frequency;
    const interval = state.interval;

    if (freq === "Daily") {
      minutes = `*/${interval}`;
    } else if (freq === "Weekly") {
      minutes = "0"; // run at the start of each hour
      hours = "0"; // midnight
      dayOfWeek = state.daysOfWeek
        .map((day) => {
          // Map short names to cron format (0 = Sunday, 6 = Saturday)
          const map = { Su: 0, M: 1, T: 2, W: 3, Th: 4, F: 5, S: 6 };
          return map[day];
        })
        .join(",");
    } else if (freq === "Monthly") {
      dayOfMonth = `1/${interval}`; // every Nth month on the 1st
      minutes = "0";
      hours = "0";
    } else if (freq === "Yearly") {
      dayOfMonth = "1";
      month = `*/${interval}`;
      minutes = "0";
      hours = "0";
    }

    return `${seconds} ${minutes} ${hours} ${dayOfMonth} ${month} ${dayOfWeek}`;
  };

  // Format date from YYYY-MM-DD to MM/DD/YYYY for display
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${month}/${day}/${year}`;
  };

  const cron = getCronExpression();
  const handleDone = () => {
    console.log("Recurring event configuration:", state);
    console.log("Generated cron expression:", cron);
    if (onDone) onDone(cron); // send to parent
    setIsOpen(false);
  };

  return (
    <div>
      <button
        className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-md text-gray-800 font-medium transition-colors"
        onClick={() => setIsOpen(true)}
      >
        Configure Frequency
      </button>

      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-md shadow-lg w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-xl font-semibold">Repeat</h2>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setIsOpen(false)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="flex items-center gap-2">
                <label
                  htmlFor="repeats"
                  className="w-24 text-sm font-medium text-gray-700"
                >
                  Scheduler:
                </label>
                <select
                  id="repeats"
                  className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={state.frequency}
                  onChange={(e) =>
                    setState({ ...state, frequency: e.target.value })
                  }
                >
                  <option value="Daily">Daily</option>
                  <option value="Weekly">Weekly</option>
                  <option value="Monthly">Monthly</option>
                  <option value="Yearly">Yearly</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <label
                  htmlFor="repeat-every"
                  className="w-24 text-sm font-medium text-gray-700"
                >
                  Repeat every:
                </label>
                <input
                  id="repeat-every"
                  type="number"
                  min={1}
                  className="w-16 rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={state.interval}
                  onChange={(e) =>
                    setState({
                      ...state,
                      interval: Number.parseInt(e.target.value) || 1,
                    })
                  }
                />
                <span className="text-sm text-gray-700">
                  {state.interval === 1
                    ? state.frequency.toLowerCase()
                    : `${state.frequency.toLowerCase()}s`}
                </span>
              </div>

              {state.frequency === "Weekly" && (
                <div className="flex items-start gap-2">
                  <label className="w-24 pt-2 text-sm font-medium text-gray-700">
                    Repeat on:
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {["M", "T", "W", "Th", "F", "S", "Su"].map((day) => (
                      <div
                        key={day}
                        className="flex justify-center items-center h-full space-x-2"
                      >
                        <input
                          type="checkbox"
                          id={`day-${day}`}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          checked={state.daysOfWeek.includes(day)}
                          onChange={() => handleDayToggle(day)}
                        />
                        <label
                          htmlFor={`day-${day}`}
                          className="text-sm text-gray-700 pt-2"
                        >
                          {day}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2">
                <label
                  htmlFor="starts-on"
                  className="w-24 text-sm font-medium text-gray-700"
                >
                  Starts on:
                </label>
                <input
                  type="date"
                  id="starts-on"
                  className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={state.startDate}
                  onChange={(e) =>
                    setState({ ...state, startDate: e.target.value })
                  }
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Ends:
                </label>
                <div className="space-y-2 ml-2">
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="end-never"
                      name="end-type"
                      className="text-blue-600 focus:ring-blue-500 h-4 w-4"
                      checked={state.endType === "never"}
                      onChange={() => setState({ ...state, endType: "never" })}
                    />
                    <label
                      htmlFor="end-never"
                      className="text-sm text-gray-700 pt-2"
                    >
                      Never
                    </label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="end-after"
                      name="end-type"
                      className="text-blue-600 focus:ring-blue-500 h-4 w-4"
                      checked={state.endType === "after"}
                      onChange={() => setState({ ...state, endType: "after" })}
                    />
                    <label
                      htmlFor="end-after"
                      className="text-sm text-gray-700 pt-2"
                    >
                      After
                    </label>
                    <input
                      type="number"
                      min={1}
                      className="w-16 rounded-md border border-gray-300 py-1 px-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={state.occurrences}
                      onChange={(e) =>
                        setState({
                          ...state,
                          occurrences: Number.parseInt(e.target.value) || 1,
                        })
                      }
                      disabled={state.endType !== "after"}
                    />
                    <span className="text-sm text-gray-700">occurrences</span>
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="end-on"
                      name="end-type"
                      className="text-blue-600 focus:ring-blue-500 h-4 w-4"
                      checked={state.endType === "on"}
                      onChange={() => setState({ ...state, endType: "on" })}
                    />
                    <label
                      htmlFor="end-on"
                      className="text-sm text-gray-700 pt-2"
                    >
                      On
                    </label>
                    <input
                      type="date"
                      className="rounded-md border border-gray-300 py-1 px-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={state.endDate}
                      onChange={(e) =>
                        setState({ ...state, endDate: e.target.value })
                      }
                      disabled={state.endType !== "on"}
                    />
                  </div>
                </div>
              </div>

              <div
                style={{ backgroundColor: "#423f3f" }}
                className="text-white p-2 rounded-md font-mono text-lg overflow-x-auto"
              >
                {cron}
              </div>

              <div className="pt-4 border-t">
                <label className="block text-sm font-medium text-gray-700">
                  Summary:
                </label>
                <p className="text-sm text-gray-600">{getSummary()}</p>
              </div>
            </div>

            <div className="flex justify-end gap-2 p-4 border-t">
              <button
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-md text-gray-800 font-medium transition-colors"
                onClick={() => setIsOpen(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-md text-white font-medium transition-colors"
                onClick={handleDone}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
